import {
  CommonListType,
  DespatchAdviceItem,
  InboundingList,
  InventoryChangeReason,
  ListProductStatus,
} from "__graphql__/types";
import { SerializedListVerificationCheck } from "flows/Inbound/models/listVerificationCheck/types";
import { TranslatedProductSearchUnit, UnitType } from "shared/models/productSearchUnit/types";

export type DroppingListSortOrder = "asc" | "desc";

export type InboundUnitsMap = Record<string, TranslatedProductSearchUnit>;

export type ClaimedProductsMap = Record<string, TranslatedClaimedProduct>;

export type StockUpdatePlan = {
  reason: InventoryChangeReason;
  quantityDelta: number;
};

export type InboundUnitStockUpdate = {
  stockUpdatePlan: StockUpdatePlan[];
  stockUpdated: boolean;
};

export type InboundUnitDisplay = {
  unitSizeForDisplay: number;
};

export type ExpectedDesadvs = {
  unitType: UnitType;
  handlingUnitSize: number;
  unitSizeForDisplay: number;
  totalQuantity: number;
};

export type InboundUnitsStockUpdates = Record<string, InboundUnitStockUpdate>;
export type InboundUnitsDisplays = Record<string, InboundUnitDisplay>;
export type ExpectedDesadvsSummary = Record<string, ExpectedDesadvs>;

export type InboundUIState = {
  isQuantityEditActive: boolean;
  currentLetterRangeId: string;
  isScanningFailedModalVisible: boolean;
  isPartialOutboundAnimationVisible: string | null;
  skuFocusedForQuantityInput: string | null | undefined;
  isHUSizeEditActive: boolean;
  isDespatchAdviceModalVisible: boolean;
  isDespatchAdviceInfoBannerVisible: boolean;
  isProductAlreadyAddedBannerVisible: boolean;
  isUnverifiedQuantityModalVisible: boolean;
  isAddToSharedListModalVisible: boolean;
  isListValidationActive: boolean;
  isUnclaimListConfirmationModalVisible: boolean;
  isUnclaimedListConfirmationModalVisible: boolean;
  isSharedListNameEditActive: boolean;
  isDeleteFinalItemModalVisibleForSku: string | null;
  isClaimedQuantityEditActiveForSku: string | null;
  isOutboundItemRemovalWarningModalVisible: boolean;
  isRefetchClaimedListConfirmationModalVisible: boolean;
  isSSCCMatchConfirmationModalVisible: boolean;
  activeItemId: string | null;
  isScrollToActiveItem: boolean;
  isClaimedListNotFoundWarningModalVisible: boolean;
  isOrderCanceled: boolean;
  isUnclaimListModalVisible: string | null;
  isNewProductsModalVisible: boolean;
  isQuantityCheckRequired: boolean;
  isProductNotInDroppingList: boolean;
  isProductAlreadyInbounded: boolean;
};

export type DespatchSummaryItem = {
  unitType: UnitType;
  handlingUnitSize: number;
  unitSizeForDisplay: number;
  totalQuantity: number;
};

export type TranslatedProduct = {
  productName: string | null;
  productSku: string;
  productImageUrl: string | null;
  isProductBio: boolean | null;
  countryOfOriginName: string | null;
  shelfLetter: string | null;
  shelfNumber: string | null;
  totalInboundQuantity: number;
  handlingUnitSize: number;
  unitSizeForDisplay: number;
  status: ListProductStatus | null;
  isInbounded: boolean;
  stockOnShelf: number;
};

export type ClaimedProductForSorting = {
  name: string | null;
  sku: string;
  shelf: string | null;
  shelfLetter: string | null;
  shelfNumber: string | null;
  eans: string[];
};

export type TranslatedClaimedProduct = {
  productId: string;
  productName: string | null;
  productSku: string;
  productImageUrl: string | null;
  shelf: string | null;
  shelfLetter: string | null;
  shelfNumber: string | null;
  numberOfShelfFacings: number;
  numberOfShelfStackings: number | null;
  isShelvedInHandlingUnits: boolean;
  countryOfOriginName: string | null;
  countryOfOriginCode: string | null;
  isProductBio: boolean | null;
  unitType: UnitType;
  handlingUnitSize: number;
  totalInboundQuantity: number;
  unitSizeForDisplay: number;
  stockOnShelf: number;
  status?: ListProductStatus | null;
  eans: string[];
  stockUpdated: boolean;
  isQuantityConfirmed: boolean;
};

export type CategorizedLists = {
  draft: InboundingList[];
  unclaimed: InboundingList[];
};

export type MatchedCategorizedList = {
  deliveryType: DeliveryType | null;
  sscc?: string;
  list: InboundingList | null;
};

export type SelectedProduct = {
  sku: string;
  handlingUnitSize: number;
  quantity: number;
};

export type InboundStoreContext = {
  deliverySSCC: string | null;
  scannedSSCC: string | null;
  deliveryType: DeliveryType | null;
  creatorId: string | null;
  orderId: string | null;
  categorizedLists: CategorizedLists;
  matchedCategorizedList: MatchedCategorizedList | null;
  sharedListName: string | null;
  sharedListId: string | null;
  inboundUnitsMap: InboundUnitsMap;
  inboundUnitsSortedByDate: string[];
  inboundUnitsStockUpdates: InboundUnitsStockUpdates;
  inboundUnitsDisplayStates: InboundUnitsDisplays;
  expectedDesadvsSummary: ExpectedDesadvsSummary;
  outboundUnclaimedSku?: string;
  shelfStockLevels: Record<string, number>;
  verificationChecks: SerializedListVerificationCheck[];
  despatchAdviceItems: Record<string, DespatchAdviceItem[]>;
  skusWithUnconfirmedQuantities: string[];
  inboundPreviewProducts: TranslatedProduct[];
  droppingListShelfSortOrder: DroppingListSortOrder;
  droppingListId: string | null;
  droppingListName: string | null;
  droppingListType: CommonListType;
  claimedProductsMap: ClaimedProductsMap;
  claimedProductsSortedByShelf: ClaimedProductForSorting[];
  claimedProductsStockUpdates: InboundUnitsStockUpdates;
  outboundClaimedSku?: string;
  selectedProduct?: SelectedProduct | null;
  inboundUIState: InboundUIState;
};

export type ClaimedProductsState = Pick<
  InboundStoreContext,
  | "droppingListId"
  | "droppingListName"
  | "droppingListType"
  | "claimedProductsMap"
  | "claimedProductsSortedByShelf"
  | "claimedProductsStockUpdates"
>;

export enum InboundUnitAddOrigin {
  Scan = "scan",
  TextSearch = "textSearch",
  NoEANSearch = "noEANSearch",
}

export enum DeliveryType {
  REWE = "REWE",
  Lekkerland = "Lekkerland",
  OtherDelivery = "other-delivery",
  NotDelivery = "not-delivery",
}

export enum InboundingType {
  ReinboundedOrder = "reinbounded_order",
}

export type DeliveryStore = {
  selectedOption: DeliveryType | null;
  deliverySSCC: string | null;
  sharedListName: string | null;
  sharedListId: string | null;
};

export enum AdditionalOutboundReason {
  missing_or_incorrect_label = "missing_or_incorrect_label",
  packaging_issue = "packaging_issue",
  physically_damage = "physically_damage",
  perished_mold = "perished_mold",
  wrong_bbd = "wrong_bbd",
}

export type ProductComplaintMetadata = {
  lotNumber: string;
  expirationDate: string;
  imageUrls: string[];
  additionalOutboundReason: AdditionalOutboundReason | null;
};
